import { useEffect, useRef, useState } from 'react';
import { Flex, Skeleton, Tabs } from 'antd';

import { MoreOutlined, StarOutlined } from '@ant-design/icons';
import FeatherIcon from 'feather-icons-react';
import { COLORS } from '@next-is-free-monorepo/globals';
import { GoalType, ShopProfile } from '@next-is-free-monorepo/next-is-free-types';
import useShopAttrs from '../../utils/hooks/useShopAttrs';
import { useSuspenseQuery } from '@tanstack/react-query';
import { profileQueryOptions, profileQuickLinksQueryOptions } from '../../utils/queryOptions';
import { IconLink, IconMapBolt, IconMapPin, IconPhone, IconPin, IconPoint } from '@tabler/icons-react';

interface GoalBarPreviewProps {
  profile?: ShopProfile & { image_preview_url: undefined | string };
  previewFormData: undefined | 'profile' | 'quick-links';
}

const GoalBarPreview = () => {

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const elementRef = useRef<any>(null);

  const onMouseDown = (e: any) => {
    const div = elementRef.current;
    setIsDragging(true);
    setStartX(e.pageX - div.offsetLeft);
    setStartY(e.pageY - div.offsetTop);
    setScrollLeft(div.scrollLeft);
    setScrollTop(div.scrollTop);
  };

  const onMouseLeave = () => {
    setIsDragging(false);
  };

  const onMouseUp = () => {
    setIsDragging(false);
  };

  const onMouseMove = (e: any) => {
    if (!isDragging) return;
    const div = elementRef.current;
    const x = e.pageX - div.offsetLeft;
    const y = e.pageY - div.offsetTop;
    const walkX = (x - startX) * 1; // Adjust the multiplier for faster/slower scrolling
    const walkY = (y - startY) * 1; // Adjust the multiplier for faster/slower scrolling
    div.scrollLeft = scrollLeft - walkX;
    div.scrollTop = scrollTop - walkY;
  };

  const scale = 1;
  const shopAttrs = useShopAttrs();
  const shopProfile = useSuspenseQuery(profileQueryOptions());
  const quickLinks = useSuspenseQuery(profileQuickLinksQueryOptions());

  const [highestNumber, setHighestNumber] = useState(0);
  const [orderedGoals, setOrderedGoals] = useState<GoalType[]>([]);
  const [imageLoading, setImageLoading] = useState(true);


  const bio = shopProfile.data.bio;
  const brand_color = shopProfile.data.brand_color;
  const goals = shopProfile.data.goals;
  const initial_points = shopProfile.data.initial_points;
  const points_ratio = shopProfile.data.points_ratio;
  const imagePreviewUrl = shopProfile.data.logo_preview_url;

  function sortGoals(goals: GoalType[]) {
    const validatedGoals = goals.filter(
      (item) => item && item?.number && item?.title
    );
    const nextGoals = validatedGoals.sort((a, b) => a.number - b.number);

    if (nextGoals.length > 0) {
      const highest = nextGoals[nextGoals.length - 1].number;
      setHighestNumber(highest);
      setOrderedGoals(nextGoals);
    } else {
      setHighestNumber(0);
      setOrderedGoals([]);
    }
  }

  useEffect(() => {
    if (goals?.length > 0) {
      sortGoals(goals);
    } else {
      setHighestNumber(0);
      setOrderedGoals([]);
    }
  }, [goals]);


  return (
    <>
      <div style={{
        width: '100%',
        aspectRatio: 1.4,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: brand_color,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {imagePreviewUrl && (
          <>
            {imageLoading &&
              <div style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#f7f7f7',
                position: 'absolute'
              }}>
                <Skeleton.Node style={{ width: 400, height: 400 }} fullSize={true} active>
                  <div />
                </Skeleton.Node>
              </div>
            }
            <img draggable={false} onLoad={() => setImageLoading(false)} alt={'logo'}
                 style={{ objectFit: 'cover', width: '100%', aspectRatio: 1.4 }}
                 src={imagePreviewUrl} />
          </>
        )}
      </div>
      <div className={'mt-6'}>
        <div className={'px-4 mb-3 flex items-center justify-between'} style={{}}>
          <p className={'text-[22px] font-bold'}>{shopAttrs.data.business_name}</p>
          <div className={'rounded-full bg-slate-100 w-8 h-8 flex justify-center items-center'}>
            <FeatherIcon size={16} icon={'more-horizontal'} />
          </div>
        </div>
        <p className={'text-slate-500 line-clamp-4 leading-[1.2] text-[13px] px-4'}>{bio}</p>
        <div className={'px-4 mt-3 grid grid-cols-2'}>
          <p className={'flex items-center gap-1 text-[13px] font-medium'}><IconMapPin size={15} strokeWidth={2.5}/> Punti vendita</p>
          {quickLinks?.data?.links && quickLinks?.data?.links.length > 0 &&
            <p className={'flex items-center gap-1 text-[13px] font-medium'}><IconLink size={15} strokeWidth={2.5}/> Links</p>
          }
          {quickLinks?.data?.phone_numbers && quickLinks?.data?.phone_numbers.length > 0 &&
            <p className={'flex items-center gap-1 text-[13px] font-medium'}><IconPhone size={15} strokeWidth={2.5}/> Chiama</p>
          }
        </div>
        <div className={'flex px-4 gap-3 mt-5 border-b'}>
          <p className={'border-b-black border-b-2 pb-1 font-medium'}>Raccolta punti</p>
          <p className={'text-slate-500'}>Novità</p>
          <p className={'text-slate-500'}>Buoni sconto</p>
        </div>
        <div className={'mt-6 mb-7'}>
          <p style={{
            paddingRight: 15,
            textAlign: 'right',
            marginBottom: 10,
            fontWeight: 500,
            color: brand_color,
            fontSize: 18 / scale
          }}>{initial_points + '/' + highestNumber}</p>
          <div style={{ paddingLeft: 15 / scale, paddingRight: 15 / scale }}>
            <div className="previewBarW bg-slate-100"
                 style={{ width: '100%', height: 40 / scale, position: 'relative', overflow: 'hidden' }}>
              <div style={{
                backgroundColor: brand_color,
                width: initial_points / highestNumber * 100 + '%',
                height: '100%',
                borderRadius: 200
              }}>
              </div>
              <div style={
                {
                  width: '100%',
                  height: 4,
                  position: 'absolute',
                  top: 'calc(50% - 7px)'
                }
              }>
                {orderedGoals.map((item, index) => {
                    const percentage = item.number / highestNumber * 100 + '%';
                    return (
                      <div key={item.title + index} style={{
                        width: 4,
                        height: 14,
                        borderRadius: 20,
                        /* backgroundColor: lighterBrandColor,*/
                        position: 'absolute',
                        left: percentage
                      }} />
                    );
                  }
                )}

              </div>
            </div>
          </div>
          <div className={'previewGoalCardsW'} onMouseDown={onMouseDown}
               onMouseLeave={onMouseLeave}
               onMouseUp={onMouseUp}
               onMouseMove={onMouseMove} ref={elementRef}
               style={{ position: 'relative', display: 'flex', gap: 10 / scale, overflowX: 'scroll', justifyContent: 'flex-start' }}>
            {orderedGoals.map((item, index) => {
                return (
                  <div key={index} style={{
                    flex: 1,
                    fontSize: 14 / scale,
                    padding: 10,
                    minWidth: 'calc(100% / 2.5)',
                    maxWidth: 'calc(100% / 2.5)',
                  /*  backgroundColor: item.number > initial_points ? COLORS.lightGray : lighterBrandColor,*/
                    borderRadius: 16 / scale,
                    color: item.number > initial_points ? COLORS.gray : brand_color,
                    aspectRatio: 1,
                    marginTop: 20,
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'column',
                    borderStyle: 'solid',
                    borderWidth: 2,
                    borderColor: item.number > initial_points ? COLORS.lightGray : brand_color,
                    position: 'relative',
                    marginLeft: index === 0 ? 15 / scale : 0,
                    marginRight: index === orderedGoals.length - 1 ? 15 / scale : 0
                  }}
                  >
                    <div style={{
                      backgroundColor: item.number > initial_points ? COLORS.gray : brand_color,
                      width: 25,
                      height: 25,
                      borderRadius: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      {item.number > initial_points ?
                        <img alt={'lock'} src={'/lock.svg'} /> :
                        <FeatherIcon icon={'star'} size={12} />
                      }
                    </div>
                    <div>
                      <div style={{
                        fontSize: 15,
                        fontWeight: 300
                      }}>
                        {item.number}
                      </div>
                      {item.title ?
                        <p style={{ fontSize: 16, lineHeight: 1.2, fontWeight: 600 }}>{item.title}</p> :
                        <p><i>Nome premio</i></p>
                      }
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
      <div style={{
        backgroundColor: brand_color,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 60 / scale,
        paddingBottom: 60 / scale,
        display: 'flex',
        flexDirection: 'column',
        gap: 20 / scale
      }}>
        <div style={{ padding: 20 / scale, borderRadius: 20 / scale, backgroundColor: 'white' }}>
          <img draggable={false} alt={'dummy-qr-code'} width={240 / scale} src={'/dummy-qr-code.png'} />
        </div>
        <p style={{
          width: 280 / scale,
          textAlign: 'center',
          color: 'white',
          fontWeight: 500,
          fontSize: 16 / scale
        }}>Mostra il codice alla cassa per ottenere punti</p>
      </div>
    </>
  );
};
export default GoalBarPreview;
